"use client";
import * as React from "react";
import cn from "clsx";
import * as AspectRatio from "@radix-ui/react-aspect-ratio";
import { Image } from "../image/image";
import { SanityImage, SimpleImage } from "../image/image-types";

const DEFAULT_VARIANT: string = "square";

export type ImageModuleProps = {
  variant?:
    | "square"
    | "circle"
    | "landscape"
    | "portrait"
    | "dome-narrow"
    | "dome-wide"
    | "half-moon-left-wide"
    | "half-moon-left"
    | "half-moon-right"
    | "pill"
    | "drop";
  ratio?: number;
  src?: SanityImage | string | undefined;
  alt?: string;
  imageText?: string;
  imageCredit?: string;
  textPosition?: "under" | "left" | "right";
  customWidth?: number;
  sizes?: string;
  loading?: "lazy" | "eager";
  style?: React.CSSProperties;
  className?: string;
} & Omit<AspectRatio.AspectRatioProps, "ratio">;

const ImageModule = React.forwardRef<HTMLDivElement, ImageModuleProps>(
  (
    {
      className,
      variant = DEFAULT_VARIANT,
      ratio,
      imageCredit,
      src,
      alt,
      imageText,
      style,
      textPosition = "under",
      customWidth,
      sizes,
      loading,
      ...props
    },
    ref
  ) => {
    const variantRatio = {
      square: 1,
      circle: 1,
      landscape: 3 / 2,
      portrait: 2 / 3,
      "dome-narrow": 1,
      "dome-wide": 3 / 2,
      "half-moon-left-wide": 1,
      "half-moon-left": 2 / 3,
      "half-moon-right": 2 / 3,
      pill: 2 / 3,
      drop: 1,
    };

    return (
      <div className={cn("in-image-module__text-position",
        (imageCredit || imageText) && `in-image-module__text-position--${textPosition}`,
      )}>
        <div
          className={cn(
            "in-image-module",
            !ratio && `in-image-module__shape--${variant}`,
            className
          )}
          style={{ ...style }}
        >
          <AspectRatio.Root
            ref={ref}
            {...props}
            ratio={ratio ?? variantRatio[variant]}
          >
            <Image
              src={src}
              alt={alt}
              customWidth={customWidth}
              sizes={sizes}
              aspectRatio={ratio ?? variantRatio[variant]}
              loading={loading}
            />
          </AspectRatio.Root>
        </div>
        {imageText || imageCredit ? (
          <div className={cn("in-image-module__meta",`in-image-module__meta__text-position--${textPosition}`)}>
            {imageText && (
              <div className="in-image-module__meta__text">{imageText}</div>
            )}
            {imageCredit && (
              <div className={cn(textPosition === "under" && !imageText && "in-image-module__meta__photo-credit",)}>
                {`© ${imageCredit}`}
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  }
);

ImageModule.displayName = "ImageModule";

export { ImageModule };
